import { Variant } from '../models/product/dto/variant';
import { Move } from './sort-utils';
import { SortVariantUtils } from './sort-variant-utils';
import { LocationPriceStream } from '../models/enum/shared/location-price-stream';
import type { SectionWithProducts } from '../models/menu/section/section-with-products';
import {
  SectionSortAdditionalDetails,
  SectionSortOption,
  SectionSortProductInfo,
  SectionSortSecondaryCannabinoids,
  SectionSortTerpenes
} from '../models/enum/dto/section-sort-option.enum';
import { Menu } from '../models/menu/menu';
import { Cannabinoid, SecondaryCannabinoid } from '../models/enum/shared/cannabinoid';
import { exists } from '../functions/exists';

// @dynamic
export class SortVariantsInSectionUtils extends SortVariantUtils {
  /* *******************************************************************************************
   *                            Section Variants - Sort Options                                *
   * *******************************************************************************************/

  static sectionVariantsByBrandAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsBrandAsc(a, b);
  };
  static sectionVariantsByBrandDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsBrandDesc(a, b);
  };

  static sectionVariantsCannabinoidAsc = (a: Variant, b: Variant, cannabinoid: string): Move => {
    return SortVariantsInSectionUtils.variantsCannabinoidSortAsc(a, b, cannabinoid);
  };
  static sectionVariantsCannabinoidDesc = (a: Variant, b: Variant, cannabinoid: string): Move => {
    return SortVariantsInSectionUtils.variantsCannabinoidSortDesc(a, b, cannabinoid);
  };

  static sectionVariantsTACAsc = (
    a: Variant,
    b: Variant,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[]
  ): Move => {
    return SortVariantsInSectionUtils.variantsTACAsc(a, b, enabledSecondaryCannabinoids);
  };
  static sectionVariantsTACDesc = (
    a: Variant,
    b: Variant,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[]
  ): Move => {
    return SortVariantsInSectionUtils.variantsTACDesc(a, b, enabledSecondaryCannabinoids);
  };

  static sectionVariantsTopCannabinoidAsc = (
    a: Variant,
    b: Variant,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredListCannabinoids: Cannabinoid[]
  ): Move => {
    return SortVariantsInSectionUtils.variantsTopCannabinoidAsc(
      a,
      b,
      enabledSecondaryCannabinoids,
      ignoredListCannabinoids
    );
  };
  static sectionVariantsTopCannabinoidDesc = (
    a: Variant,
    b: Variant,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredListCannabinoids: Cannabinoid[]
  ): Move => {
    return SortVariantsInSectionUtils.variantsTopCannabinoidDesc(
      a,
      b,
      enabledSecondaryCannabinoids,
      ignoredListCannabinoids
    );
  };

  static sectionVariantsTerpeneAsc = (a: Variant, b: Variant, terpeneCamelCased: string): Move => {
    return SortVariantsInSectionUtils.variantsTerpeneSortAsc(a, b, terpeneCamelCased);
  };
  static sectionVariantsTerpeneDesc = (a: Variant, b: Variant, terpeneCamelCased: string): Move => {
    return SortVariantsInSectionUtils.variantsTerpeneSortDesc(a, b, terpeneCamelCased);
  };

  static sectionVariantsTATAsc = (a: Variant, b: Variant, enabledTerpenesPascalCased: string[]): Move => {
    return SortVariantsInSectionUtils.variantsTATAsc(a, b, enabledTerpenesPascalCased);
  };
  static sectionVariantsTATDesc = (a: Variant, b: Variant, enabledTerpenesPascalCased: string[]): Move => {
    return SortVariantsInSectionUtils.variantsTATDesc(a, b, enabledTerpenesPascalCased);
  };

  static sectionVariantsTopTerpeneAsc = (a: Variant, b: Variant, enabledTerpenesPascalCased: string[]): Move => {
    return SortVariantsInSectionUtils.variantsTopTerpeneAsc(a, b, enabledTerpenesPascalCased);
  };
  static sectionVariantsTopTerpeneDesc = (a: Variant, b: Variant, enabledTerpenesPascalCased: string[]): Move => {
    return SortVariantsInSectionUtils.variantsTopTerpeneDesc(a, b, enabledTerpenesPascalCased);
  };

  static sectionVariantsByClassificationAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.strainTypeSortAsc(a?.classification, b?.classification);
  };
  static sectionVariantsByClassificationDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.strainTypeSortDesc(a?.classification, b?.classification);
  };

  static sectionVariantsByLineageAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsLineageAsc(a, b);
  };
  static sectionVariantsByLineageDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsLineageDesc(a, b);
  };

  static sectionVariantsByManufacturerAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsManufacturerAsc(a, b);
  };
  static sectionVariantsByManufacturerDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsManufacturerDesc(a, b);
  };

  static sectionVariantsByPackagedQuantityAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsPackagedQuantityAsc(a, b);
  };
  static sectionVariantsByPackagedQuantityDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsPackagedQuantityDesc(a, b);
  };

  static sectionVariantsByPriceAsc = (
    [a, b]: [Variant, Variant],
    [themeId, locId, companyId]: [string, number, number],
    [locPriceStream, hideSale]: [LocationPriceStream, boolean]
  ): Move => {
    return SortVariantsInSectionUtils.variantsPriceAsc([a, b], [themeId, locId, companyId], [locPriceStream, hideSale]);
  };
  static sectionVariantsByPriceDesc = (
    [a, b]: [Variant, Variant],
    [themeId, locId, compId]: [string, number, number],
    [locPriceStream, hideSale]: [LocationPriceStream, boolean]
  ): Move => {
    return SortVariantsInSectionUtils.variantsPriceDesc([a, b], [themeId, locId, compId], [locPriceStream, hideSale]);
  };

  static sectionVariantsByProductTypeAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsProductTypeAsc(a, b);
  };
  static sectionVariantsByProductTypeDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsProductTypeDesc(a, b);
  };

  static sectionVariantsBySecondaryPriceAsc = (
    [a, b]: [Variant, Variant],
    [themeId, locationId, companyId]: [string, number, number],
    [section, locationPriceStream, hideSale]: [SectionWithProducts, LocationPriceStream, boolean]
  ): Move => {
    return SortVariantsInSectionUtils.variantsSecondaryPriceAsc(
      [a, b],
      [themeId, locationId, companyId],
      [section, locationPriceStream, hideSale]
    );
  };
  static sectionVariantsBySecondaryPriceDesc = (
    [a, b]: [Variant, Variant],
    [themeId, locationId, companyId]: [string, number, number],
    [section, locationPriceStream, hideSale]: [SectionWithProducts, LocationPriceStream, boolean]
  ): Move => {
    return SortVariantsInSectionUtils.variantsSecondaryPriceDesc(
      [a, b],
      [themeId, locationId, companyId],
      [section, locationPriceStream, hideSale]
    );
  };

  static sectionVariantsByStockAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsStockAsc(a, b);
  };
  static sectionVariantsByStockDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsStockDesc(a, b);
  };

  static sectionVariantsByTitleAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsTitleAsc(a, b);
  };
  static sectionVariantsByTitleDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsTitleDesc(a, b);
  };

  static sectionVariantsByUnitSizeAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsUnitSizeAsc(a, b);
  };
  static sectionVariantsByUnitSizeDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsUnitSizeDesc(a, b);
  };

  static sectionVariantsVariantTypeAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsVariantTypeAsc(a, b);
  };
  static sectionVariantsVariantTypeDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsVariantTypeDesc(a, b);
  };

  static sectionVariantsByEffectsAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsEffectsAsc(a, b);
  };
  static sectionVariantsByEffectsDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsEffectsDesc(a, b);
  };

  static sectionVariantsByAromasAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsAromasAsc(a, b);
  };
  static sectionVariantsByAromasDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsAromasDesc(a, b);
  };

  static sectionVariantsByFlavorGroupAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsFlavorGroupAsc(a, b);
  };
  static sectionVariantsByFlavorGroupDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsFlavorGroupDesc(a, b);
  };

  static sectionVariantsByFlavorsAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsFlavorsAsc(a, b);
  };
  static sectionVariantsByFlavorsDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsFlavorsDesc(a, b);
  };

  static sectionVariantsByHelpsWithAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsHelpsWithAsc(a, b);
  };
  static sectionVariantsByHelpsWithDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsHelpsWithDesc(a, b);
  };

  static sectionVariantsByAppearancesAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsAppearancesAsc(a, b);
  };
  static sectionVariantsByAppearancesDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsAppearancesDesc(a, b);
  };

  static sectionVariantsByGrowingConditionsAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsGrowingConditionsAsc(a, b);
  };
  static sectionVariantsByGrowingConditionsDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsGrowingConditionsDesc(a, b);
  };

  static sectionVariantsByEnergyScoreAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsEnergyScoreAsc(a, b);
  };
  static sectionVariantsByEnergyScoreDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsEnergyScoreDesc(a, b);
  };

  static sectionVariantsByCraftDesignationsAsc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsCraftDesignationsAsc(a, b);
  };
  static sectionVariantsByCraftDesignationsDesc = (a: Variant, b: Variant): Move => {
    return SortVariantsInSectionUtils.variantsCraftDesignationsDesc(a, b);
  };

  static sectionVariantsByTagsAsc = (a: Variant, b: Variant, section: SectionWithProducts): Move => {
    return SortVariantsInSectionUtils.variantsTagsAsc(a, b, section);
  };
  static sectionVariantsByTagsDesc = (a: Variant, b: Variant, section: SectionWithProducts): Move => {
    return SortVariantsInSectionUtils.variantsTagsDesc(a, b, section);
  };

  static sectionVariantsByTagGroupsAsc = (a: Variant, b: Variant, section: SectionWithProducts): Move => {
    return SortVariantsInSectionUtils.variantsTagGroupsAsc(a, b, section);
  };
  static sectionVariantsByTagGroupsDesc = (a: Variant, b: Variant, section: SectionWithProducts): Move => {
    return SortVariantsInSectionUtils.variantsTagGroupsDesc(a, b, section);
  };

  private static sectionVariantSorter(
    sortOption: SectionSortOption,
    [a, b]: [Variant, Variant],
    [themeId, locId, compId]: [string, number, number],
    [section, locationPriceStream, hideSale]: [SectionWithProducts, LocationPriceStream, boolean]
  ): Move {
    switch (true) {
      case Object.values(SectionSortProductInfo).includes(sortOption as SectionSortProductInfo):
        return SortVariantsInSectionUtils.sectionVariantSorterProductInfoMove(
          sortOption,
          [a, b],
          [themeId, locId, compId],
          [section, locationPriceStream, hideSale]
        );
      case Object.values(SectionSortAdditionalDetails).includes(sortOption as SectionSortAdditionalDetails):
        return SortVariantsInSectionUtils.sectionVariantSorterAdditionalDetailsMove(sortOption, section, a, b);
      case Object.values(SectionSortSecondaryCannabinoids).includes(sortOption as SectionSortSecondaryCannabinoids):
        return SortVariantsInSectionUtils.sectionVariantSorterSecondaryCannabinoidMove(sortOption, a, b);
      case Object.values(SectionSortTerpenes).includes(sortOption as SectionSortTerpenes):
        return SortVariantsInSectionUtils.sectionVariantSorterTerpeneMove(sortOption, a, b);
    }
  }

  private static sectionVariantSorterProductInfoMove(
    sortOption: SectionSortOption,
    [a, b]: [Variant, Variant],
    [themeId, locId, compId]: [string, number, number],
    [section, locationPriceStream, hideSale]: [SectionWithProducts, LocationPriceStream, boolean]
  ): Move {
    switch (sortOption) {
      case SectionSortProductInfo.BrandAsc:
        return SortVariantsInSectionUtils.sectionVariantsByBrandAsc(a, b);
      case SectionSortProductInfo.BrandDesc:
        return SortVariantsInSectionUtils.sectionVariantsByBrandDesc(a, b);
      case SectionSortProductInfo.CBDAsc:
        return SortVariantsInSectionUtils.sectionVariantsCannabinoidAsc(a, b, 'CBD');
      case SectionSortProductInfo.CBDDesc:
        return SortVariantsInSectionUtils.sectionVariantsCannabinoidDesc(a, b, 'CBD');
      case SectionSortProductInfo.ClassificationAsc:
        return SortVariantsInSectionUtils.sectionVariantsByClassificationAsc(a, b);
      case SectionSortProductInfo.ClassificationDesc:
        return SortVariantsInSectionUtils.sectionVariantsByClassificationDesc(a, b);
      case SectionSortProductInfo.LineageAsc:
        return SortVariantsInSectionUtils.sectionVariantsByLineageAsc(a, b);
      case SectionSortProductInfo.LineageDesc:
        return SortVariantsInSectionUtils.sectionVariantsByLineageDesc(a, b);
      case SectionSortProductInfo.ManufacturerAsc:
        return SortVariantsInSectionUtils.sectionVariantsByManufacturerAsc(a, b);
      case SectionSortProductInfo.ManufacturerDesc:
        return SortVariantsInSectionUtils.sectionVariantsByManufacturerDesc(a, b);
      case SectionSortProductInfo.PackagedQuantityAsc:
        return SortVariantsInSectionUtils.sectionVariantsByPackagedQuantityAsc(a, b);
      case SectionSortProductInfo.PackagedQuantityDesc:
        return SortVariantsInSectionUtils.sectionVariantsByPackagedQuantityDesc(a, b);
      case SectionSortProductInfo.PriceAsc:
        return SortVariantsInSectionUtils.sectionVariantsByPriceAsc(
          [a, b],
          [themeId, locId, compId],
          [locationPriceStream, hideSale]
        );
      case SectionSortProductInfo.PriceDesc:
        return SortVariantsInSectionUtils.sectionVariantsByPriceDesc(
          [a, b],
          [themeId, locId, compId],
          [locationPriceStream, hideSale]
        );
      case SectionSortProductInfo.ProductTypeAsc:
        return SortVariantsInSectionUtils.sectionVariantsByProductTypeAsc(a, b);
      case SectionSortProductInfo.ProductTypeDesc:
        return SortVariantsInSectionUtils.sectionVariantsByProductTypeDesc(a, b);
      case SectionSortProductInfo.SecondaryPriceAsc:
        return SortVariantsInSectionUtils.sectionVariantsBySecondaryPriceAsc(
          [a, b],
          [themeId, locId, compId],
          [section, locationPriceStream, hideSale]
        );
      case SectionSortProductInfo.SecondaryPriceDesc:
        return SortVariantsInSectionUtils.sectionVariantsBySecondaryPriceDesc(
          [a, b],
          [themeId, locId, compId],
          [section, locationPriceStream, hideSale]
        );
      case SectionSortProductInfo.StockAsc:
        return SortVariantsInSectionUtils.sectionVariantsByStockAsc(a, b);
      case SectionSortProductInfo.StockDesc:
        return SortVariantsInSectionUtils.sectionVariantsByStockDesc(a, b);
      case SectionSortProductInfo.TACAsc:
        return SortVariantsInSectionUtils.sectionVariantsTACAsc(a, b, section?.enabledSecondaryCannabinoids);
      case SectionSortProductInfo.TACDesc:
        return SortVariantsInSectionUtils.sectionVariantsTACDesc(a, b, section?.enabledSecondaryCannabinoids);
      case SectionSortProductInfo.TitleAsc:
        return SortVariantsInSectionUtils.sectionVariantsByTitleAsc(a, b);
      case SectionSortProductInfo.TitleDesc:
        return SortVariantsInSectionUtils.sectionVariantsByTitleDesc(a, b);
      case SectionSortProductInfo.THCAsc:
        return SortVariantsInSectionUtils.sectionVariantsCannabinoidAsc(a, b, 'THC');
      case SectionSortProductInfo.THCDesc:
        return SortVariantsInSectionUtils.sectionVariantsCannabinoidDesc(a, b, 'THC');
      case SectionSortProductInfo.TopCannabinoidAsc:
        return SortVariantsInSectionUtils.sectionVariantsTopCannabinoidAsc(
          a,
          b,
          section?.enabledSecondaryCannabinoids ?? [],
          section?.ignoredListCannabinoids ?? []
        );
      case SectionSortProductInfo.TopCannabinoidDesc:
        return SortVariantsInSectionUtils.sectionVariantsTopCannabinoidDesc(
          a,
          b,
          section?.enabledSecondaryCannabinoids ?? [],
          section?.ignoredListCannabinoids ?? []
        );
      case SectionSortProductInfo.TopTerpeneAsc:
        return SortVariantsInSectionUtils.sectionVariantsTopTerpeneAsc(a, b, section?.enabledTerpenesPascalCased);
      case SectionSortProductInfo.TopTerpeneDesc:
        return SortVariantsInSectionUtils.sectionVariantsTopTerpeneDesc(a, b, section?.enabledTerpenesPascalCased);
      case SectionSortProductInfo.TotalTerpeneAsc:
        return SortVariantsInSectionUtils.sectionVariantsTATAsc(a, b, section?.enabledTerpenesPascalCased);
      case SectionSortProductInfo.TotalTerpeneDesc:
        return SortVariantsInSectionUtils.sectionVariantsTATDesc(a, b, section?.enabledTerpenesPascalCased);
      case SectionSortProductInfo.UnitSizeAsc:
        return SortVariantsInSectionUtils.sectionVariantsByUnitSizeAsc(a, b);
      case SectionSortProductInfo.UnitSizeDesc:
        return SortVariantsInSectionUtils.sectionVariantsByUnitSizeDesc(a, b);
      case SectionSortProductInfo.VariantTypeAsc:
        return SortVariantsInSectionUtils.sectionVariantsVariantTypeAsc(a, b);
      case SectionSortProductInfo.VariantTypeDesc:
        return SortVariantsInSectionUtils.sectionVariantsVariantTypeDesc(a, b);
      default:
        return SortVariantsInSectionUtils.variantsTitleAsc(a, b);
    }
  }

  private static sectionVariantSorterAdditionalDetailsMove(
    sortOption: SectionSortOption,
    section: SectionWithProducts,
    a: Variant,
    b: Variant
  ): Move {
    switch (sortOption) {
      case SectionSortAdditionalDetails.EffectsAsc:
        return SortVariantsInSectionUtils.sectionVariantsByEffectsAsc(a, b);
      case SectionSortAdditionalDetails.EffectsDesc:
        return SortVariantsInSectionUtils.sectionVariantsByEffectsDesc(a, b);
      case SectionSortAdditionalDetails.AromasAsc:
        return SortVariantsInSectionUtils.sectionVariantsByAromasAsc(a, b);
      case SectionSortAdditionalDetails.AromasDesc:
        return SortVariantsInSectionUtils.sectionVariantsByAromasDesc(a, b);
      case SectionSortAdditionalDetails.FlavorGroupAsc:
        return SortVariantsInSectionUtils.sectionVariantsByFlavorGroupAsc(a, b);
      case SectionSortAdditionalDetails.FlavorGroupDesc:
        return SortVariantsInSectionUtils.sectionVariantsByFlavorGroupDesc(a, b);
      case SectionSortAdditionalDetails.FlavorsAsc:
        return SortVariantsInSectionUtils.sectionVariantsByFlavorsAsc(a, b);
      case SectionSortAdditionalDetails.FlavorsDesc:
        return SortVariantsInSectionUtils.sectionVariantsByFlavorsDesc(a, b);
      case SectionSortAdditionalDetails.HelpsWithAsc:
        return SortVariantsInSectionUtils.sectionVariantsByHelpsWithAsc(a, b);
      case SectionSortAdditionalDetails.HelpsWithDesc:
        return SortVariantsInSectionUtils.sectionVariantsByHelpsWithDesc(a, b);
      case SectionSortAdditionalDetails.AppearancesAsc:
        return SortVariantsInSectionUtils.sectionVariantsByAppearancesAsc(a, b);
      case SectionSortAdditionalDetails.AppearancesDesc:
        return SortVariantsInSectionUtils.sectionVariantsByAppearancesDesc(a, b);
      case SectionSortAdditionalDetails.GrowingConditionsAsc:
        return SortVariantsInSectionUtils.sectionVariantsByGrowingConditionsAsc(a, b);
      case SectionSortAdditionalDetails.GrowingConditionsDesc:
        return SortVariantsInSectionUtils.sectionVariantsByGrowingConditionsDesc(a, b);
      case SectionSortAdditionalDetails.EnergyScoreAsc:
        return SortVariantsInSectionUtils.sectionVariantsByEnergyScoreAsc(a, b);
      case SectionSortAdditionalDetails.EnergyScoreDesc:
        return SortVariantsInSectionUtils.sectionVariantsByEnergyScoreDesc(a, b);
      case SectionSortAdditionalDetails.CraftDesignationsAsc:
        return SortVariantsInSectionUtils.sectionVariantsByCraftDesignationsAsc(a, b);
      case SectionSortAdditionalDetails.CraftDesignationsDesc:
        return SortVariantsInSectionUtils.sectionVariantsByCraftDesignationsDesc(a, b);
      case SectionSortAdditionalDetails.TagsAsc:
        return SortVariantsInSectionUtils.sectionVariantsByTagsAsc(a, b, section);
      case SectionSortAdditionalDetails.TagsDesc:
        return SortVariantsInSectionUtils.sectionVariantsByTagsDesc(a, b, section);
      case SectionSortAdditionalDetails.TagGroupsAsc:
        return SortVariantsInSectionUtils.sectionVariantsByTagGroupsAsc(a, b, section);
      case SectionSortAdditionalDetails.TagGroupsDesc:
        return SortVariantsInSectionUtils.sectionVariantsByTagGroupsDesc(a, b, section);
      default:
        return SortVariantsInSectionUtils.variantsTitleAsc(a, b);
    }
  }

  private static sectionVariantSorterSecondaryCannabinoidMove(
    sortOption: SectionSortOption,
    a: Variant,
    b: Variant
  ): Move {
    const { cannabinoid, order } = SortVariantsInSectionUtils.getCannabinoidAccessor(sortOption);
    const isAscending = order === 'ASC';
    return isAscending
      ? SortVariantsInSectionUtils.sectionVariantsCannabinoidAsc(a, b, cannabinoid)
      : SortVariantsInSectionUtils.sectionVariantsCannabinoidDesc(a, b, cannabinoid);
  }

  private static sectionVariantSorterTerpeneMove(sortOption: SectionSortOption, a: Variant, b: Variant): Move {
    const { terpeneCamelCased, order } = SortVariantsInSectionUtils.getTerpeneAccessor(sortOption);
    const isAscending = order === 'ASC';
    return isAscending
      ? SortVariantsInSectionUtils.sectionVariantsTerpeneAsc(a, b, terpeneCamelCased)
      : SortVariantsInSectionUtils.sectionVariantsTerpeneDesc(a, b, terpeneCamelCased);
  }

  /**
   * Generic, can sort a list of variants or a list of variant groups.
   * If there is no secondary sort, then the tertiary sort becomes the secondary sort.
   * Don't do the tertiary sort if it's the same as secondary sort, otherwise always do tertiary sort.
   * Short-circuit evaluation used to prevent unnecessary function calls.
   * Primary sort always exists.
   * Secondary sort is optional.
   * Tertiary sort always exists and is hard-coded to title asc.
   *
   * @param sorter - the function that is responsible for sorting the variants
   * @param data - the list of variants or variant groups to sort
   * @param menu - the menu the variants belong to
   * @param sec - the section the variants belong to
   * @param priceStream - the price format that the location is using
   * @returns the sorted list of variants or variant groups
   */
  protected static sortVariantsInSection<T>(
    sorter: (
      sortOption: SectionSortOption,
      [a, b]: [T, T],
      [tId, lId, cId]: [string, number, number],
      [section, locationPriceStreamForSort, hideSaleForSort]: [SectionWithProducts, LocationPriceStream, boolean]
    ) => Move,
    data: T[],
    menu: Menu,
    sec: SectionWithProducts,
    priceStream: LocationPriceStream
  ): T[] {
    const primary = sec?.sorting;
    const secondary = sec?.secondarySorting;
    const hasSecondary = exists(secondary);
    const tertiary = SortVariantsInSectionUtils.DEFAULT_SECTION_TERTIARY_SORT;
    const tId = menu?.theme;
    const lId = menu?.locationId;
    const cId = menu?.companyId;
    const hideSale = menu?.menuOptions?.hideSale ?? false;
    const primarySortId = SortVariantsInSectionUtils.sharedSortId(primary);
    const secondarySortId = SortVariantsInSectionUtils.sharedSortId(secondary);
    const tertiarySortId = SortVariantsInSectionUtils.sharedSortId(tertiary);
    const sortByTertiary = secondarySortId !== tertiarySortId;
    const sortVariantsInSection = (a: T, b: T) => {
      // short-circuit calculation
      return (
        sorter(primary, [a, b], [tId, lId, cId], [sec, priceStream, hideSale]) ||
        (hasSecondary ? sorter(secondary, [a, b], [tId, lId, cId], [sec, priceStream, hideSale]) : Move.Nothing) ||
        (sortByTertiary ? sorter(tertiary, [a, b], [tId, lId, cId], [sec, priceStream, hideSale]) : Move.Nothing)
      );
    };
    return data?.sort(sortVariantsInSection) || [];
  }

  static sortVariantsBySectionSortOptions = (
    variants: Variant[],
    menu: Menu,
    section: SectionWithProducts,
    locationPriceStream: LocationPriceStream
  ): Variant[] => {
    const sorter = SortVariantsInSectionUtils.sectionVariantSorter;
    return SortVariantsInSectionUtils.sortVariantsInSection(sorter, variants, menu, section, locationPriceStream);
  };
}
